import React, { useEffect } from "react";
import { Grid, Icon, IconButton, MenuItem, TextField } from "@material-ui/core";
import { Field } from "formik";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";

import {
  addNewDiscount,
  fillAgentFormField,
  fillDiscountField,
  removeDiscount,
  resetDiscount,
} from "../store/agent-form.slice";
import { AppState } from "../../../store";
import { fetchAllCurrencies } from "../../flights/store/currencies-list.slice";
import useAuthority from "app/hooks/useAuthority";
import { ROLES } from "app/constants/roles";


const DiscountInfo = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const {hasUserAnyAuthority} = useAuthority();
  const agentForm = useSelector((state: AppState) => state.agents.agentForm);
  const { currencies } = useSelector(
    (state: AppState) => state.flights.currenciesList
  );

  const isAdmin = hasUserAnyAuthority([ROLES.ROLE_ADMIN]);

  const getDefaultCurrency = () => {
    const defaultCurrency = currencies?.find((item) => item.isoCode === "EUR");
    return defaultCurrency ? defaultCurrency.id : "";
  };

  useEffect(() => {
    if (!currencies?.length) return;

    const first = agentForm.discounts?.[0];
    if (!first) return;

    if (first.currencyId === "") {
      dispatch(
        fillDiscountField({
          id: first.id,
          field: "currencyId",
          value: getDefaultCurrency(),
        })
      );
    }
  }, [currencies, agentForm.discounts, dispatch]);

  useEffect(() => {
    dispatch(fetchAllCurrencies());
  }, [dispatch]);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    id?: string
  ) => {
    if (!isAdmin) return;

    dispatch(
      fillDiscountField({
        value:
          e.target.name === "currencyId" ? e.target.value : +e.target.value,
        field: e.target.name,
        id,
      })
    );
  };

  const preventInvalidNumberInput = (
    e: any
  ) => {
    if (
      e.key === "-" ||
      e.key === "e" ||
      e.key === "E" ||
      e.code === "Minus"
    ) {
      e.preventDefault();
    }
  };

  return (
    <div>
      <Grid container spacing={4}>
        <Grid item xs={12}>
          <Field
            as={TextField}
            style={{ width: "100%" }}
            label={t(
              "app.main.agency.create.discountInfo.label1",
              "Allow discount on ticket (Maximum)"
            )}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              if (!isAdmin) return;

              dispatch(
                fillAgentFormField({
                  field: "discountType",
                  value: e.target.value,
                })
              );
              dispatch(resetDiscount(getDefaultCurrency()));
            }}
            value={agentForm.discountType}
            variant="outlined"
            size="small"
            name="discountType"
            select
            disabled={!isAdmin}
          >
            <MenuItem value="NO_DISCOUNT">
              {t(
                "app.main.agency.create.discountInfo.label2",
                "Do not allow discount"
              )}
            </MenuItem>
            <MenuItem value="ALLOW_DISCOUNT">
              {t(
                "app.main.agency.create.discountInfo.label3",
                "Allow discount"
              )}
            </MenuItem>
            <MenuItem value="AUTOMATIC">
              {t(
                "app.main.agency.create.discountInfo.label4",
                "Make automatic discount"
              )}
            </MenuItem>
          </Field>
        </Grid>
      </Grid>

      {agentForm.discountType !== "NO_DISCOUNT" &&
        agentForm.discounts.map((discount) => {
          return (
            <Grid container spacing={4} key={discount.id}>
              {agentForm.discountType === "ALLOW_DISCOUNT" && (
                <Grid item xs={3}>
                  <Field
                    as={TextField}
                    style={{ width: "100%", marginRight: "20px" }}
                    label={t(
                      "app.main.agency.create.discountInfo.label5",
                      "Maximum discount"
                    )}
                    onChange={(
                      e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
                    ) => {
                      handleChange(e, discount.id);
                    }}
                    variant="outlined"
                    size="small"
                    name="maxDiscount"
                    value={discount.maxDiscount}
                    type="number"
                    disabled={!isAdmin}
                    InputProps={{ inputProps: { min: 0 } }}
                    onKeyDown={preventInvalidNumberInput}
                  />
                </Grid>
              )}

              {agentForm.discountType === "AUTOMATIC" && (
                <Grid item xs={3}>
                  <Field
                    as={TextField}
                    style={{ width: "100%", marginRight: "20px" }}
                    label={t(
                      "app.main.agency.create.discountInfo.label6",
                      "Automatic discount"
                    )}
                    onChange={(
                      e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
                    ) => {
                      handleChange(e, discount.id);
                    }}
                    variant="outlined"
                    size="small"
                    name="automaticDiscount"
                    value={discount.automaticDiscount}
                    type="number"
                    disabled={!isAdmin}
                    InputProps={{ inputProps: { min: 0 } }}
                    onKeyDown={preventInvalidNumberInput}
                  />
                </Grid>
              )}

              <Grid item xs={3}>
                <Field
                  as={TextField}
                  style={{ width: "100%", marginRight: "20px" }}
                  label={t(
                    "app.main.agency.create.discountInfo.label7",
                    "Discount that have a price greater than"
                  )}
                  onChange={(
                    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
                  ) => {
                    handleChange(e, discount.id);
                  }}
                  variant="outlined"
                  size="small"
                  name="minimumTicketPrice"
                  value={discount.minimumTicketPrice}
                  type="number"
                  disabled={!isAdmin}
                  InputProps={{ inputProps: { min: 0 } }}
                  onKeyDown={preventInvalidNumberInput}
                />
              </Grid>

              <Grid item xs={3}>
                <div style={{ display: "flex" }}>
                  <Field
                    as={TextField}
                    onChange={(
                      e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
                    ) => handleChange(e, discount.id)}
                    variant="outlined"
                    value={discount.currencyId}
                    name="currencyId"
                    label={t("app.main.report.search.currency", "Currency")}
                    select
                    style={{ flex: 1, minWidth: 120 }}
                    size="small"
                    disabled={!isAdmin}
                  >
                    {currencies &&
                      currencies
                        .filter((cr) =>
                          ["EUR", "CHF", "USD"].includes(cr.isoCode)
                        )
                        .map((cr: any, idx) => (
                          <MenuItem key={idx} value={cr.id}>
                            {cr.name} - {cr.isoCode}
                          </MenuItem>
                        ))}
                  </Field>

                  <IconButton
                    style={{ margin: "0 10px" }}
                    onClick={() => {
                      if (!isAdmin) return;
                      dispatch(addNewDiscount(getDefaultCurrency()));
                    }}
                    size="small"
                    disabled={!isAdmin}
                  >
                    <Icon>add</Icon>
                  </IconButton>

                  <IconButton
                    onClick={() => {
                      if (!isAdmin) return;
                      dispatch(removeDiscount(discount.id));
                    }}
                    size="small"
                    disabled={!isAdmin || agentForm.discounts.length === 1}
                  >
                    <Icon color="error">delete</Icon>
                  </IconButton>
                </div>
              </Grid>
            </Grid>
          );
        })}
    </div>
  );
};

export default DiscountInfo;